import { useCallback, useEffect, useRef, useState } from "react";

type ScrollDirection = "up" | "down" | null;

const DEFAULT_THRESHOLD = 10;

/**
 * スクロール方向を検出するカスタムフック。
 * requestAnimationFrame でスロットリングし、passive リスナーでパフォーマンスを保証する。
 *
 * @param threshold - 方向切り替えに必要な最小スクロール量（px）。デフォルト 10px。
 * @returns "up" | "down" | null（初期状態 or ページ最上部）
 */
export function useScrollDirection(
  threshold: number = DEFAULT_THRESHOLD
): ScrollDirection {
  const [direction, setDirection] = useState<ScrollDirection>(null);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const updateDirection = useCallback(() => {
    const currentScrollY = window.scrollY;

    if (currentScrollY <= 0) {
      setDirection(null);
      lastScrollY.current = 0;
      ticking.current = false;
      return;
    }

    const diff = currentScrollY - lastScrollY.current;

    if (Math.abs(diff) >= threshold) {
      setDirection(diff > 0 ? "down" : "up");
      lastScrollY.current = currentScrollY;
    }

    ticking.current = false;
  }, [threshold]);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(updateDirection);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      // removeEventListener は capture フラグのみ照合するため passive 不要だが、対称性のためコメント記載
      window.removeEventListener("scroll", handleScroll);
    };
  }, [updateDirection]);

  return direction;
}
